import { CosmicModuleType, ModuleType } from '@finn/ui-cosmic';
import { useEffect, useRef, useState } from 'react';

import CosmicModule from '~/ui-modules/CosmicModule';

/**
  This hook returns sticky element that PageBodyBuilder renders on desktop only,
  as on mobile we do not want stickeness. It also returns starter and stopper ref 
  which pagebodybuilder will render based on cosmic data and assign refs to them.
  Later in this hook we use ref to calculate height and top of the sticky element.
  To implement boundaries of scroll, sadly we can not do it with css only, as
  our layout does not allow it yet. Hopefully later we will improve layout and remove JS part
*/

export const useStickyConversionElement = ({
  modules,
}: {
  modules: ModuleType[];
}) => {
  const [height, setHeight] = useState('0px');
  const [top, setTop] = useState('0px');
  const [isReady, setIsReady] = useState(false);
  const stopperRef = useRef<HTMLDivElement>(null);
  const starterRef = useRef<HTMLDivElement>(null);
  const conversionElements = modules?.filter(
    (module) => module.type === CosmicModuleType.WEB_MODULE_CONVERSIONELEMENT
  );

  useEffect(() => {
    // arbitary number, likely 50ms is enough, but to be safe we wait for 800ms
    // it is purely depends on how much time react needs to render after the cycle that
    // rendered table of contents
    const WAIT_FOR_TABLE_OF_CONTENTS = 500;
    const timer = setTimeout(() => {
      const starterTop = starterRef?.current?.offsetTop || 208;
      const stopperTop = stopperRef?.current?.offsetTop || 0;

      const nextHeight = stopperTop - starterTop;
      // 56 is a top that we want to have between screen and sticky element
      setHeight(`${nextHeight}px`);
      setTop(`${starterTop}px`);
      setIsReady(true);
    }, WAIT_FOR_TABLE_OF_CONTENTS);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return {
    stopperRef,
    starterRef,
    conversionElements,
    stickyConversionElement: conversionElements?.length ? (
      <div
        className="absolute right-[calc(50%-636px)] hidden h-full w-[232px] md:block"
        style={{ height, top, opacity: isReady ? 1 : 0 }}
      >
        <div className="z-sticky container sticky top-0 hidden w-full flex-col items-end pt-14 md:flex md:w-[calc(100%-1rem)] md:max-w-[726px]">
          {conversionElements?.map((conversionElement, index) => (
            <CosmicModule
              key={`${conversionElement?.id}-${index}`}
              data={{
                ...conversionElement,
                metadata: {
                  ...conversionElement.metadata,
                  index,
                  sticky: true,
                },
              }}
            />
          ))}
        </div>
      </div>
    ) : null,
  };
};
